import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection } from './LegalLayout';

export default function Terms() {
  return (
    <LegalLayout title="Terms of Use" updated="September 1, 2026" testId="terms-page">
      <LegalSection title="Agreement">
        <p>
          These Terms of Use (&quot;Terms&quot;) govern access to Sourcetape at sourcetape.io,
          including Studio, the API, credit packs, and the forthcoming desktop app. Sourcetape is
          operated by Alex Casanova (&quot;Sourcetape,&quot; &quot;we,&quot; &quot;us&quot;). By
          using the service you agree to these Terms and the{' '}
          <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
          If you do not agree, do not use the service.
        </p>
      </LegalSection>

      <LegalSection title="The service">
        <p>
          Sourcetape is a quality harness for AI agent skills. You describe an outcome, test and
          score a skillset, iterate on the skill text, and export portable{' '}
          <code className="text-textp">SKILL.md</code>. It is not a skills marketplace, not an
          agent runtime, and not model training. Local schema tests stay free. Hosted scores,
          judges, and improvement loops spend credits.
        </p>
      </LegalSection>

      <LegalSection title="Eligibility and accounts">
        <p>
          You must be at least 16 and able to form a contract. You may browse and use guest Studio
          without an account. An account (email and password, or Google SSO) is required for saved
          projects, a credit wallet, and hosted runs. You are responsible for the credentials on
          that account and for activity under it. Email us if you believe the account is
          compromised.
        </p>
      </LegalSection>

      <LegalSection title="Credits and payments">
        <p>
          Credit packs (Starter, Builder, Lab) are prepaid. Stripe processes the charge. Credits
          land after checkout completes. A failed or cancelled payment never credits the balance.
          One credit is one baseline hosted score on a mid-size model. Heavier actions cost more.
          Pack prices and credit amounts are shown at purchase.
        </p>
        <p>
          Credits are not cash, have no cash value, and are not transferable. They are generally
          non-refundable once added, except where a refund is required by law. Lab packs have no
          SLA. We may change pack prices going forward; a completed purchase is not repriced.
        </p>
        <p>
          Subscriptions, if offered on the account page, renew until you cancel. Canceling stops
          future charges. It does not automatically refund unused credits.
        </p>
      </LegalSection>

      <LegalSection title="Your content">
        <p>
          You keep ownership of the skill text, fixtures, outcomes, and other material you put
          into Sourcetape (&quot;Your Content&quot;). You grant us a limited license to host,
          copy, and process Your Content solely to provide the service: save it, run tests,
          score it, send it to the model provider you chose, and export it when you ask.
        </p>
        <p>
          You represent that you have the rights to that material and that using it in Sourcetape
          does not violate the law or someone else&apos;s rights. Do not upload secrets you do
          not intend to send to a model provider.
        </p>
      </LegalSection>

      <LegalSection title="API keys and third-party models">
        <p>
          You may bring your own OpenAI or Anthropic keys, or use LM Studio in the desktop
          app with no key. Hosted models run on our key and spend credits. Keys you save are used
          only to fulfill requests you start. Those providers have their own terms. We are not
          responsible for their models, outages, or how they handle prompts. Hosted scores we
          run on your behalf also go to a model provider.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable use">
        <p>Do not:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Probe, disrupt, or overload the service, or bypass billing or auth.</li>
          <li>Use the service to build or operate malware, or to attack systems you do not own.</li>
          <li>Upload unlawful content, or content you do not have the right to use.</li>
          <li>Resell access, scrape the catalog at scale, or impersonate Sourcetape.</li>
          <li>Use hosted compute to violate a model provider&apos;s own acceptable-use rules.</li>
        </ul>
        <p>We may suspend or close an account that breaks these rules.</p>
      </LegalSection>

      <LegalSection title="Exports and compatibility">
        <p>
          Exported <code className="text-textp">SKILL.md</code> is yours to use with compatible
          agents (Claude Code, Cursor, Codex, and others). We do not lock the skill format behind
          a paywall. We do not warrant that a given agent will load or follow a skill, or that a
          score will stay the same after a model change.
        </p>
      </LegalSection>

      <LegalSection title="Desktop app">
        <p>
          A Mac and Windows desktop app is planned. Until downloads are offered, any &quot;Coming
          soon&quot; control is not a license to a binary. When it ships, these Terms apply to it
          as well. Local scoring on the desktop stays on your machine. Hosted loops still spend
          credits.
        </p>
      </LegalSection>

      <LegalSection title="Disclaimer">
        <p>
          The service is provided &quot;as is&quot; and &quot;as available.&quot; Scores, judges,
          and improvement loops are tools, not a guarantee that a skill is safe, correct, or fit
          for production. We disclaim warranties of merchantability, fitness for a particular
          purpose, and non-infringement, to the fullest extent the law allows.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, Sourcetape and its operator are not liable for
          indirect, incidental, special, consequential, or punitive damages, or for lost profits,
          lost data, or replacement compute. Our total liability for a claim relating to the
          service is limited to the amount you paid us for credits in the three months before
          the claim. Some places do not allow these limits. In those places, the limit is the
          minimum the law requires.
        </p>
      </LegalSection>

      <LegalSection title="Termination">
        <p>
          You may stop using Sourcetape at any time. We may suspend or close the service or an
          account if you breach these Terms, if we must for legal reasons, or if we shut the
          product down. Unused credits are not refundable on termination except where the law
          requires it. Sections that should survive (ownership, disclaimers, liability, this
          sentence) do.
        </p>
      </LegalSection>

      <LegalSection title="Changes">
        <p>
          We may update these Terms. The date at the top is the current version. Continued use
          after a change is acceptance of the new Terms. If you do not agree, stop using the
          service.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Questions about these Terms: <a href="mailto:privacy@sourcetape.io" className="text-accent hover:underline">privacy@sourcetape.io</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
